import React, { useState, useRef, useCallback } from 'react'
import { IonPage, IonButton, IonHeader, IonLoading } from '@ionic/react'
import AppContent from 'ui/layouts/AppContent'
import LoginForm from 'ui/components/forms/LoginForm'
import './Login.css'
import OfflineMessage from 'ui/layouts/headers/OfflineMessage'
import { UserCredentials } from 'core/User'
import AppLogo from 'ui/components/images/AppLogo'
import AppVersion from 'ui/components/appVersion/AppVersion'
import { AppTheme } from 'core/Theme'

interface LoginProps {
  login: (credentials: UserCredentials) => Promise<void>
  isLoginLoading: boolean
  isOffline?: boolean
  loginError: string
  signInText?: string
  usernameLabel?: string
  passwordLabel?: string
}

const Login: React.FC<LoginProps> = ({
  login,
  isLoginLoading,
  isOffline = false,
  loginError,
  signInText = 'Iniciar sesión',
  usernameLabel,
  passwordLabel,
}) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const usernameInputRef = useRef<HTMLIonInputElement>(null)
  const passwordInputRef = useRef<HTMLIonInputElement>(null)

  const handleUsername = useCallback((e: CustomEvent) => {
    setUsername(e.detail.value)
  }, [])

  const handlePassword = useCallback((e: CustomEvent) => {
    setPassword(e.detail.value)
  }, [])

  const handleSubmit = useCallback(async () => {
    // On Android, the state may not have been updated yet (especially when pasting text),
    // so we always get the values directly from the inputs
    let currentUsername = username
    let currentPassword = password

    const usernameInput = usernameInputRef.current
    const passwordInput = passwordInputRef.current

    // Try to get the values directly from the inputs
    if (usernameInput) {
      const inputElement = await usernameInput.getInputElement()
      if (inputElement && inputElement.value) {
        currentUsername = inputElement.value
      }
    }

    if (passwordInput) {
      const inputElement = await passwordInput.getInputElement()
      if (inputElement && inputElement.value) {
        currentPassword = inputElement.value
      }
    }

    await login({ username: currentUsername, password: currentPassword })
  }, [login, username, password])

  return (
    <IonPage>
      {isOffline ? (
        <IonHeader>
          <OfflineMessage />
        </IonHeader>
      ) : null}
      <AppContent>
        <IonLoading isOpen={isLoginLoading} />
        <div className="login-wrapper ion-padding-horizontal">
          <div className="logo ion-margin-vertical">
            <AppLogo />
          </div>
          <h1 className="home-title">{AppTheme.appTitle}</h1>
          <LoginForm
            error={loginError}
            onUsernameChange={handleUsername}
            onPasswordChange={handlePassword}
            onSubmit={handleSubmit}
            usernameLabel={usernameLabel}
            passwordLabel={passwordLabel}
            usernameInputRef={usernameInputRef}
            passwordInputRef={passwordInputRef}
          />
          <IonButton
            expand="block"
            size="default"
            disabled={isLoginLoading}
            onClick={handleSubmit}
            className="button"
          >
            {signInText}
          </IonButton>

          <AppVersion />
        </div>
      </AppContent>
    </IonPage>
  )
}

export default Login
